import React, { useState, useEffect, useRef } from 'react';
import { StackView, Text } from 'localmed-core';
import styled from '@emotion/styled';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { useQuery } from '@apollo/react-hooks';
import { endOfDay, startOfDay } from 'date-fns';
import { css } from '@emotion/core';

import { Alert, RadioGroup } from '../../../shared/components/DiComponents';
import { formatDate, formatOpeningTime } from '../../../shared/utils/formatCalendarTime';
import { DATE_FORMAT, LM_CLIENT_CONTEXT } from '../../../shared/constants';
import DiErrorModal from '../../../shared/components/DiComponents/DiModal/DiErrorModal';
import supportsTimeZoneFormat from '../../../shared/utils/supportsTimeZoneFormat';
import {
  addCurrentAppointmentAsOpening,
  removeUnavailableOpenings,
} from '../../../shared/components/RescheduleForm/utils';

import query from './queries/SchedulingOpeningsQuery.graphql';
import type { IOpening, ThemeProps } from '../../../types';
import { ISchedulingValues } from '../../pages/SchedulingPage/components/schedulingWizard';
import { DiGenericLoading } from '../../../shared/components/DiLayout';
import { RENDER_PHASE, UniversalContext } from '../../../_universal';
import { getTimeZoneName, isCurrentTimeZone } from '../../../legacyCore/OpeningsCalendar/dateUtils';
import getTimeZoneFormat from '../../../shared/utils/getTimeZoneFormat';

const TimeSelectionWrapper = styled(StackView)(
  ({ theme }: ThemeProps) => css`
    max-width: ${theme.breakpoints.md};
  `
);

const ButtonWrapper = styled(StackView)(
  ({ theme, selected, width }: any) => css`
    border-radius: ${theme.radii.lg};
    border: 1px solid ${theme.colors.lightGray};
    padding: ${theme.space.xs};
    margin-right: ${theme.space.sm};
    width: ${width || 'auto'};
    background-color: ${selected ? theme.colors.blue : 'transparent'};
    color: ${selected ? theme.colors.white : theme.colors.black};

    &:hover {
      border: 1px solid ${theme.colors.blue};
    }
  `
);

interface IProps {
  intl: IntlShape;
  values: ISchedulingValues;
  setValue: (name: string, value: any) => void;
  currentDate: Date;
  nextOpening?: IOpening;
  datePickerLoading: boolean;
  unavailableOpenings?: Array<IOpening>;
}

function isEmpty(values?: any[]): boolean {
  return !values || values.length === 0;
}

function SchedulingOpeningSection({
  intl,
  values,
  currentDate,
  datePickerLoading,
  setValue,
  unavailableOpenings = [],
}: IProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const openingsRef = useRef<any>([]);
  const universalContext = React.useContext<any>(UniversalContext);

  const isClientRender =
    universalContext == null || universalContext.renderPhase === RENDER_PHASE.CLIENT_RENDER;
  const {
    opening,
    provider,
    queryProvider,
    reasonForVisit,
    office,
    openingError,
    appointment,
  } = values;

  const RadioButton = ({ time, selected }) => (
    <ButtonWrapper
      selected={selected}
      justifyContent="center"
      spacing="xs"
      direction="row"
      width="70px"
      fontSize="xs"
    >
      {formatOpeningTime(time, intl, getTimeZoneFormat(office?.timeZoneCode))}
    </ButtonWrapper>
  );

  const openingQueryVars = queryProvider?.id
    ? { providerId: queryProvider?.id }
    : { officeId: office?.id };

  const { data: openingsData, loading, error } = useQuery(query, {
    variables: {
      ...openingQueryVars,
      reasonForVisitId: reasonForVisit?.id,
      before: endOfDay(currentDate).toISOString(),
      after: startOfDay(currentDate).toISOString(),
    },
    context: LM_CLIENT_CONTEXT,
  });

  if (openingsData?.openings) {
    if (appointment) {
      openingsRef.current = addCurrentAppointmentAsOpening(
        removeUnavailableOpenings(openingsData?.openings.nodes || [], unavailableOpenings),
        appointment
      );
    } else {
      openingsRef.current = removeUnavailableOpenings(
        openingsData?.openings.nodes || [],
        unavailableOpenings
      );
    }
  }

  useEffect(() => {
    if (error) {
      setModalOpen(true);
    }
  }, [error]);

  if (modalOpen) {
    return (
      <DiErrorModal
        open
        closeModal={() => {
          setModalOpen(false);
        }}
        text="We could not load the available times."
      />
    );
  }

  const noOpenings =
    !loading && isEmpty(openingsRef.current) && !openingsData?.pageInfo?.nextAvailableDate;

  let content;

  if (loading) {
    content = !datePickerLoading ? <DiGenericLoading /> : null;
  } else if (isEmpty(openingsRef.current) || noOpenings) {
    content = (
      <StackView spacing="md">
        <Text>
          <FormattedMessage
            id="scheduling.Schedulingtimeselectionstep.noAvailableMessage"
            defaultMessage="{providerSelected, select, true {{providerDisplay} does not} false {We do not}} have any online openings for {reasonForVisitDisplay} this day."
            values={{
              providerSelected: provider != null,
              providerDisplay: provider?.display,
              reasonForVisitDisplay: reasonForVisit?.name || ' your reason for visit',
            }}
          />
        </Text>
      </StackView>
    );
  } else {
    content = (
      <RadioGroup
        maxWidth={{ _: '260px', lg: '100%' }}
        justifyContent="flex-end"
        name="times"
        items={openingsRef.current}
        itemDisplayComponent={RadioButton}
        onChange={(newOpening: IOpening) => {
          setValue('opening', newOpening);
          setValue('openingError', false);
          if (newOpening.provider.id !== provider?.id) {
            setValue('provider', newOpening.provider);
          }
        }}
        value={opening}
      />
    );
  }

  let AlertContent;

  if (isClientRender && !isCurrentTimeZone(office?.timeZoneCode)) {
    if (supportsTimeZoneFormat()) {
      const firstTime = openingsRef.current.length && openingsRef.current[0].time;
      const timeZoneName =
        getTimeZoneName(intl, firstTime, office?.timeZoneCode) || office?.timeZoneCode;
      AlertContent = (
        <Alert type="warning" p="md" my="md" hideTitle inline>
          <FormattedMessage
            id="scheduling.Schedulingtimeselectionstep.localTimeZoneMessage"
            defaultMessage="These open appointments are in a different time zone: {timeZoneName}"
            values={{
              timeZoneName,
            }}
          />
        </Alert>
      );
    } else {
      AlertContent = (
        <Alert type="warning" p="md" my="md" inline>
          <FormattedMessage
            id="scheduling.Schedulingtimeselectionstep.localTimeZoneMessage"
            defaultMessage="These open appointments are shown in your local time zone, which may be different from the
          office’s."
          />
        </Alert>
      );
    }
  }

  return (
    <TimeSelectionWrapper spacing="md" ml={{ _: 'none', lg: 'xl' }}>
      {openingError && (
        <Alert type="error" p="md" m="md">
          <FormattedMessage
            id="scheduling.Schedulingtimeselectionstep.openingErrorMessage"
            defaultMessage="This time is no longer available."
          />
        </Alert>
      )}
      {AlertContent}
      <Text fontWeight="700" fontSize="sm" textAlign="center">
        <FormattedMessage
          id="scheduling.Schedulingtimeselectionstep.availableTimesMessage"
          defaultMessage="Available Times"
        />{' '}
        {currentDate
          ? `for ${formatDate(intl, currentDate, null, {
              intl: DATE_FORMAT.LONG,
              fallback: DATE_FORMAT.LONG_FALLBACK,
            })}`
          : ':'}
      </Text>
      {content}
    </TimeSelectionWrapper>
  );
}

export default injectIntl(SchedulingOpeningSection);
